// licenseSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { licenseAPI } from "./api";
import { logout } from "../auth/index";
import { createMiddleware } from "../middlewareUtil";
import { cloneDeep } from "lodash";

// Initial state
const initialState = {
  loading: false,
  loaded: false,
  saving: false,
  saved: false,
  status: {
    valid: false,
  },
};

export const loadLicense = createAsyncThunk(
  "license/loadLicense",
  async (_, { getState, dispatch, rejectWithValue }) => {
    const accessToken = getState().auth.accessCode;

    try {
      const result = await licenseAPI.loadLicense(accessToken);
      if (result.success) {
        return result.data;
      } else if (result.error === "unauthorized") {
        dispatch(logout());
        return rejectWithValue("Authentication failed");
      }
      return rejectWithValue("Failed to load license");
    } catch (error) {
      console.error("[loadLicense] Error loading license:", error);
      return rejectWithValue("Server error");
    }
  }
);

export const uploadLicense = createAsyncThunk(
  "license/uploadLicense",
  async (licenseJson, { getState, dispatch, rejectWithValue }) => {
    const accessToken = getState().auth.accessCode;

    try {
      const result = await licenseAPI.uploadLicense(accessToken, licenseJson);
      if (result.success) {
        return result.data;
      } else if (result.error === "unauthorized") {
        dispatch(logout());
        return rejectWithValue("Authentication failed");
      }
      return rejectWithValue("Failed to upload license");
    } catch (error) {
      console.error("[uploadLicense] Error uploading license:", error);
      return rejectWithValue("Server error");
    }
  }
);

// Create the slice
const licenseSlice = createSlice({
  name: "license",
  initialState,
  reducers: {
    reset: () => cloneDeep(initialState),
  },
  extraReducers: (builder) => {
    builder
      // Load license
      .addCase(loadLicense.pending, (state, _action) => {
        state.loading = true;
        state.loaded = false;
        state.saving = false;
        state.saved = false;
      })
      .addCase(loadLicense.fulfilled, (state, action) => {
        state.status = action.payload;
        state.loading = false;
        state.loaded = true;
        state.saving = false;
        state.saved = false;
      })
      .addCase(loadLicense.rejected, (state, _action) => {
        state.loading = false;
        state.loaded = false;
      })

      // Upload license
      .addCase(uploadLicense.pending, (state, _action) => {
        state.saving = true;
        state.saved = false;
      })
      .addCase(uploadLicense.fulfilled, (state, action) => {
        state.status = action.payload;
        state.saving = false;
        state.saved = true;
      })
      .addCase(uploadLicense.rejected, (state, _action) => {
        state.saving = false;
        state.saved = false;
      });
  },
});

export const licenseMiddleware = createMiddleware([
  "license/loadLicense/pending",
  "license/uploadLicense/pending",
]);

// Export actions
export const { reset } = licenseSlice.actions;

// Export reducer
export default licenseSlice.reducer;
